import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

/**
 * Interface for Cognito JWT tokens returned after successful authentication
 */
export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

/**
 * Interface for Cognito authentication errors
 */
export interface CognitoAuthError {
  code: string;
  name: string;
  message: string;
}

/**
 * Cognito configuration for different environments
 */
interface CognitoConfig {
  userPoolId: string;
  clientId: string;
  region: string;
}

/**
 * Environment-specific Cognito configurations
 * These are hardcoded in login-ui and detected at runtime based on URL
 * Note: Empty strings indicate Cognito is not yet configured for that environment
 */
const COGNITO_CONFIGS: Record<string, CognitoConfig> = {
  production: {
    userPoolId: "", // TODO: Replace with actual production pool ID when ready
    clientId: "", // TODO: Replace with actual production client ID when ready
    region: "eu-west-1",
  },
  staging: {
    userPoolId: "eu-west-1_eaaoxNZK9",
    clientId: "49njjfaf764q1st8psr8rhm36t",
    region: "eu-west-1",
  },
};

/**
 * Get Cognito configuration based on runtime URL detection
 * Detects environment from window.location.host
 * Default: production config, unless URL contains "stage" or "localhost"
 */
const getCognitoConfig = (): CognitoConfig | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const useStaging =
    window.location.host.includes("stage") ||
    window.location.host.includes("localhost");
  const config = useStaging
    ? COGNITO_CONFIGS.staging
    : COGNITO_CONFIGS.production;

  // return null if config values are empty (Cognito not configured for this env)
  if (!config.userPoolId || !config.clientId) {
    return null;
  }

  return config;
};

/**
 * Initialize Cognito User Pool with runtime environment configuration
 * Environment is automatically detected from the URL hostname
 */
const getUserPool = (): CognitoUserPool | null => {
  const config = getCognitoConfig();

  if (!config) {
    console.warn(
      "Cognito configuration not available for this environment. " +
        "Cognito authentication is disabled.",
    );
    return null;
  }

  return new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.clientId,
  });
};

/**
 * Authenticate user with AWS Cognito using email and password
 *
 * @param email - User's email address
 * @param password - User's password
 * @returns Promise resolving to CognitoTokens (idToken, accessToken, refreshToken)
 * @throws CognitoAuthError if authentication fails
 *
 */
export function authenticateCognito(
  email: string,
  password: string,
): Promise<CognitoTokens> {
  return new Promise((resolve, reject) => {
    const userPool = getUserPool();

    if (!userPool) {
      reject({
        code: "ConfigurationError",
        name: "CognitoConfigurationError",
        message: "Cognito is not configured",
      } as CognitoAuthError);
      return;
    }

    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        resolve({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
      },

      onFailure: (err: CognitoAuthError) => {
        // Map common Cognito errors to consistent error format
        reject({
          code: err.code || "AuthenticationFailed",
          name: err.name || "CognitoAuthenticationError",
          message: err.message || "Authentication failed",
        } as CognitoAuthError);
      },
    });
  });
}

/**
 * Check if Cognito authentication is configured and available
 * @returns true if Cognito is properly configured, false otherwise
 */
export function isCognitoConfigured(): boolean {
  const config = getCognitoConfig();
  return config !== null;
}
